import api from './api';
import { DirectRecordingUpload } from './directRecordingUpload';

// Keep each 720p recording part comfortably below a typical reverse-proxy
// 1 MiB default body limit without lowering the final recording quality.
const RECORDING_CHUNK_INTERVAL_MS = 2_000;
const RECORDING_CHUNK_UPLOAD_ATTEMPTS = 6;

const getRecordableMedia = () => [...document.querySelectorAll('video, audio')]
  .filter((element) => element.srcObject instanceof MediaStream);

const uniqueRecordableVideos = () => {
  const seen = new Set();
  return getRecordableMedia()
    .filter((element) => element.tagName === 'VIDEO')
    .filter((video) => video.readyState >= 2 && video.videoWidth > 0)
    .filter((video) => {
      const trackId = video.srcObject?.getVideoTracks?.()[0]?.id || video.srcObject?.id;
      if (!trackId || seen.has(trackId)) return false;
      seen.add(trackId);
      return true;
    });
};

const drawContain = (context, source, x, y, width, height) => {
  const sourceWidth = source.videoWidth || source.naturalWidth || source.width;
  const sourceHeight = source.videoHeight || source.naturalHeight || source.height;
  if (!sourceWidth || !sourceHeight) return;
  const scale = Math.min(width / sourceWidth, height / sourceHeight);
  const targetWidth = sourceWidth * scale;
  const targetHeight = sourceHeight * scale;
  context.drawImage(
    source,
    x + (width - targetWidth) / 2,
    y + (height - targetHeight) / 2,
    targetWidth,
    targetHeight
  );
};

const chooseMimeType = () => [
  // VP8 is inexpensive to encode while a classroom call is already busy.
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
  'video/mp4',
].find((type) => MediaRecorder.isTypeSupported(type)) || '';

const resumeRecordingAudio = async (context) => {
  if (!context || context.state === 'running') return;
  let timer;
  try {
    await Promise.race([
      context.resume(),
      new Promise((_, reject) => { timer = window.setTimeout(() => {
        const error = new Error('Click Confirm and start to enable browser recording audio.');
        error.code = 'RECORDING_AUDIO_GESTURE_REQUIRED'; reject(error);
      }, 4000); }),
    ]);
  } finally { window.clearTimeout(timer); }
};

const wait = (milliseconds) =>
  new Promise((resolve) => window.setTimeout(resolve, milliseconds));

const isRetryableRequestError = (error) => {
  const status = Number(error?.response?.status || 0);
  return (
    !error?.response ||
    error?.code === 'ECONNABORTED' ||
    [401, 408, 425, 429].includes(status) ||
    status >= 500
  );
};

class MeetingRecorder {
  constructor() {
    this.recorder = null;
    this.recordingId = '';
    this.roomId = '';
    this.chunkIndex = 0;
    this.uploadQueue = Promise.resolve();
    this.audioContext = null;
    this.animationFrame = 0;
    this.outputStream = null;
    this.audioRefreshTimer = 0;
    this.connectedAudioTrackIds = new Set();
    this.audioSources = new Map();
    this.silentAudioSource = null;
    this.stopPromise = null;
    this.blackboardImage = null;
    this.blackboardRefreshTimer = 0;
    this.blackboardObjectUrl = '';
    this.uploadFailure = null;
    this.abortPromise = null;
    this.directUpload = null;
    this.captureStoppedPromise = null;
    this.preservePromise = null;
    this.startPromise = null;
    this.primedAudioContext = null;
  }

  refreshBlackboardFrame() {
    const svg = document.querySelector('svg[data-recording-blackboard="true"]');
    if (!svg || !svg.isConnected || svg.getClientRects().length === 0) {
      this.blackboardImage = null;
      return;
    }
    try {
      const clone = svg.cloneNode(true);
      clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      clone.setAttribute('width', '1600');
      clone.setAttribute('height', '900');
      const blob = new Blob(
        [new XMLSerializer().serializeToString(clone)],
        { type: 'image/svg+xml;charset=utf-8' }
      );
      const url = URL.createObjectURL(blob);
      const image = new Image();
      image.onload = () => {
        if (this.blackboardObjectUrl) URL.revokeObjectURL(this.blackboardObjectUrl);
        this.blackboardObjectUrl = url;
        this.blackboardImage = image;
      };
      image.onerror = () => URL.revokeObjectURL(url);
      image.src = url;
    } catch {
      // Keep recording participant video even if a malformed SVG element
      // cannot be serialized.
    }
  }

  buildCompositeStream() {
    const width = 1280;
    const height = 720;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { alpha: false });
    const canvasStream = canvas.captureStream(25);
    this.refreshBlackboardFrame();
    this.blackboardRefreshTimer = window.setInterval(
      () => this.refreshBlackboardFrame(),
      700
    );
    const draw = () => {
      context.fillStyle = '#111827';
      context.fillRect(0, 0, width, height);
      // Re-scan on every frame so participants who join after recording starts
      // are also included in the composite.
      const visible = uniqueRecordableVideos();
      const board = this.blackboardImage;

      if (board?.complete && board.naturalWidth > 0) {
        const gap = 10;
        const railWidth = Math.min(330, Math.round(width * 0.27));
        const boardWidth = width - railWidth - gap * 3;
        context.fillStyle = '#0f172a';
        context.fillRect(gap, gap, boardWidth, height - gap * 2);
        try {
          drawContain(context, board, gap, gap, boardWidth, height - gap * 2);
        } catch {}

        const columns = visible.length > 4 ? 2 : 1;
        const rows = Math.max(1, Math.ceil(visible.length / columns));
        const cellWidth = (railWidth - gap * (columns - 1)) / columns;
        const cellHeight = Math.min(170, (height - gap * (rows + 1)) / rows);
        visible.forEach((video, index) => {
          const column = index % columns;
          const row = Math.floor(index / columns);
          const x = boardWidth + gap * 2 + column * (cellWidth + gap);
          const y = gap + row * (cellHeight + gap);
          try { drawContain(context, video, x, y, cellWidth, cellHeight); } catch {}
        });
        this.animationFrame = window.setTimeout(draw, 1000 / 25);
        return;
      }
      const count = Math.max(1, visible.length);
      const columns = count <= 1 ? 1 : count <= 4 ? 2 : 3;
      const rows = Math.ceil(count / columns);
      const gap = 8;
      const cellWidth = (width - gap * (columns + 1)) / columns;
      const cellHeight = (height - gap * (rows + 1)) / rows;

      visible.forEach((video, index) => {
        const x = gap + (index % columns) * (cellWidth + gap);
        const y = gap + Math.floor(index / columns) * (cellHeight + gap);
        try { drawContain(context, video, x, y, cellWidth, cellHeight); } catch {}
      });

      if (!visible.length) {
        context.fillStyle = '#e5e7eb';
        context.font = '32px sans-serif';
        context.textAlign = 'center';
        context.fillText('Waiting for meeting video…', width / 2, height / 2);
      }
      this.animationFrame = window.setTimeout(draw, 1000 / 25);
    };
    draw();

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (AudioContextClass) {
      this.audioContext = this.primedAudioContext || new AudioContextClass();
      this.primedAudioContext = null;
      const destination = this.audioContext.createMediaStreamDestination();
      // Keep an audio clock even when everybody is muted or no microphone
      // has joined yet. Otherwise some encoders wait for their first audio
      // sample and emit no usable video at all on Stop.
      if (this.audioContext.createConstantSource) {
        this.silentAudioSource = this.audioContext.createConstantSource();
        this.silentAudioSource.offset.value = 0;
        this.silentAudioSource.connect(destination);
        this.silentAudioSource.start();
      }
      const connectNewAudioTracks = () => {
        for (const [id, entry] of this.audioSources) {
          if (entry.track.readyState === 'ended') { entry.source.disconnect(); this.audioSources.delete(id); this.connectedAudioTrackIds.delete(id); }
        }
        getRecordableMedia().forEach((element) => {
          const stream = element.srcObject;
          stream.getAudioTracks().forEach((track) => {
            if (track.readyState === 'ended' || this.connectedAudioTrackIds.has(track.id)) {
              return;
            }
            try {
              const source = this.audioContext.createMediaStreamSource(
                new MediaStream([track])
              );
              source.connect(destination);
              this.connectedAudioTrackIds.add(track.id);
              this.audioSources.set(track.id, { track, source });
            } catch {}
          });
        });
      };
      connectNewAudioTracks();
      this.audioRefreshTimer = window.setInterval(connectNewAudioTracks, 2000);
      destination.stream.getAudioTracks().forEach((track) => canvasStream.addTrack(track));
    }
    return canvasStream;
  }

  unlockRecordingAudio() {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass || this.isActive()) return Promise.resolve();
    this.primedAudioContext?.close().catch(() => {});
    this.primedAudioContext = new AudioContextClass();
    // Invoked directly from Confirm's click before any network await.
    return resumeRecordingAudio(this.primedAudioContext);
  }

  releasePrimedAudio() {
    this.primedAudioContext?.close().catch(() => {});
    this.primedAudioContext = null;
  }

  start(options = {}) {
    // The prompt, manual controls and a reconnect may all request capture in
    // the same browser. Only one initialization may reserve the server lease.
    if (this.startPromise) {
      if (String(options.roomId || '').trim() !== this.roomId) return Promise.reject(new Error('Another meeting recording is still starting. Wait for it to finish.'));
      return this.startPromise;
    }
    if (this.isActive() || this.stopPromise || this.preservePromise || this.directUpload) {
      return Promise.reject(new Error('Another recording is still capturing or saving. Wait for it to finish.'));
    }
    const promise = this.startCapture(options);
    this.startPromise = promise;
    promise.finally(() => { if (this.startPromise === promise) this.startPromise = null; }).catch(() => {});
    return promise;
  }

  async startCapture({ roomId, title = '', sessionId, automaticAttemptId }) {
    if (this.isActive() || this.stopPromise || this.preservePromise || this.directUpload) throw new Error('Another recording is still capturing or saving. Wait for it to finish.');
    if (typeof MediaRecorder === 'undefined' || typeof HTMLCanvasElement.prototype.captureStream !== 'function') {
      throw new Error('Recording is not supported in this browser. Use a current Chrome, Edge, Firefox or Safari browser with media recording enabled.');
    }
    if (!window.isSecureContext) throw new Error('Meeting recording needs HTTPS or localhost.');
    this.roomId = String(roomId || '').trim();
    if (!this.roomId) throw new Error('Room ID is required');
    const mimeType = chooseMimeType();
    try {
      this.outputStream = this.buildCompositeStream();
      if (this.audioContext?.state === 'suspended') {
        await resumeRecordingAudio(this.audioContext);
      }
      if (this.audioContext && this.audioContext.state !== 'running') {
        const error = new Error('Click Confirm and start to enable browser recording audio.');
        error.code = 'RECORDING_AUDIO_GESTURE_REQUIRED';
        throw error;
      }
      this.recorder = new MediaRecorder(this.outputStream, { ...(mimeType ? { mimeType } : {}), videoBitsPerSecond: 2_500_000, audioBitsPerSecond: 128_000 });
    } catch (error) { this.cleanup(); throw error; }
    // stop() marks the recorder inactive before its final dataavailable event.
    // Keep the event promise so every stop path waits for that last chunk.
    this.captureStoppedPromise = new Promise(resolve => {
      this.recorder.addEventListener('stop', resolve, { once: true });
    });
    const actualMimeType = this.recorder.mimeType || mimeType || 'video/webm';

    let data;
    try {
      ({ data } = await api.post(`/recordings/${encodeURIComponent(this.roomId)}/start`, {
        title,
        mimeType: actualMimeType,
        ...(sessionId ? { sessionId } : {}),
        ...(automaticAttemptId ? { automaticAttemptId } : {}),
      }));
    } catch (error) {
      this.cleanup();
      throw error;
    }
    if (!data?.recordingId) { this.cleanup(); throw new Error('The server did not create a recording. Try again.'); }
    this.recordingId = String(data.recordingId);
    this.chunkIndex = 0;
    this.uploadQueue = Promise.resolve();
    this.uploadFailure = null;
    try {
      if (data.uploadMode !== 'e2-direct') throw new Error('The server is using a retired recording transport. Refresh the app or contact the administrator.');
      if (data.uploadMode === 'e2-direct') {
        this.directUpload = await DirectRecordingUpload.create({
          recordingId: this.recordingId, roomId: this.roomId, title, mimeType: actualMimeType,
          maxBytes: data.maxBytes, partBytes: data.partBytes, storageProvider: data.storageProvider,
          transport: data.transport || 'legacy',
        });
        this.directUpload.onFailure = () => { window.setTimeout(() => { void this.preserveDirectCapture().catch(() => {}); }, 0); };
      }
      this.recorder.addEventListener('dataavailable', (event) => {
        if (!event.data?.size || this.uploadFailure) return;
        if (this.directUpload) {
          const upload = this.directUpload;
          this.uploadQueue = this.uploadQueue.then(() => upload.add(event.data)).catch(error => {
            upload.reportFailure(error);
          });
          return;
        }
        const index = this.chunkIndex++;
        this.uploadQueue = this.uploadQueue.then(async () => {
          if (this.uploadFailure) return;
          try {
            await this.uploadChunkWithRetry(event.data, index);
          } catch (error) {
            this.uploadFailure = error;
            window.dispatchEvent(new CustomEvent('meeting-recording-capture-error', {
              detail: {
                roomId: this.roomId,
                recordingId: this.recordingId,
                message:
                  error?.response?.data?.error ||
                  error?.message ||
                  'A recording chunk could not be uploaded.',
              },
            }));
            // Run after the current queue item settles so abort() can safely
            // wait for the complete queue without waiting on itself.
            window.setTimeout(() => {
              void this.abort('Recording chunk upload failed');
            }, 0);
          }
        });
      });
      this.recorder.start(RECORDING_CHUNK_INTERVAL_MS);
      // Browser/device capture failures must finish the asynchronous final
      // dataavailable event and retain every locally committed chunk for retry.
      this.recorder.addEventListener('error', (event) => {
        const upload = this.directUpload;
        upload?.reportFailure(Object.assign(event.error || new Error('Browser recording capture was interrupted.'), { captureFatal: true }));
      });
    } catch (error) {
      await this.directUpload?.discard().catch(() => {});
      await this.abortServerCapture(
        'Browser MediaRecorder could not initialize'
      ).catch(() => undefined);
      this.cleanup();
      throw error;
    }
    return { recordingId: this.recordingId };
  }

  async preserveDirectCapture() {
    if (this.preservePromise) return this.preservePromise;
    // A user stop already owns finalization and its failure path preserves data.
    if (this.stopPromise) return this.stopPromise.catch(() => {});
    const upload = this.directUpload;
    if (!upload) return;
    const recorder = this.recorder;
    const stopped = this.captureStoppedPromise;
    this.preservePromise = (async () => {
      if (recorder && recorder.state !== 'inactive') recorder.stop();
      if (stopped) await stopped;
      await this.uploadQueue.catch(() => {});
      await upload.preserve().catch(() => {});
      await this.postWithRetry(`/recordings/${encodeURIComponent(this.roomId)}/${encodeURIComponent(this.recordingId)}/stop`, {}).catch(() => {});
      this.cleanup();
    })();
    return this.preservePromise;
  }

  async uploadChunkWithRetry(
    blob,
    index,
    maxAttempts = RECORDING_CHUNK_UPLOAD_ATTEMPTS
  ) {
    let lastError;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        const formData = new FormData();
        formData.append('chunk', blob, `chunk-${index}.webm`);
        formData.append('chunkIndex', String(index));
        await api.post(
          `/recordings/${encodeURIComponent(this.roomId)}/${encodeURIComponent(this.recordingId)}/chunk`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 180_000,
          }
        );
        return;
      } catch (error) {
        lastError = error;
        if (attempt >= maxAttempts || !isRetryableRequestError(error)) throw error;
        await wait(Math.min(4000, 500 * (2 ** (attempt - 1))));
      }
    }
    throw lastError;
  }

  async postWithRetry(url, data, maxAttempts = 3) {
    let lastError;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      try {
        return await api.post(url, data);
      } catch (error) {
        lastError = error;
        if (attempt >= maxAttempts || !isRetryableRequestError(error)) throw error;
        await wait(Math.min(4000, 500 * (2 ** (attempt - 1))));
      }
    }
    throw lastError;
  }

  async abortServerCapture(reason) {
    if (!this.roomId || !this.recordingId) return null;
    return this.postWithRetry(
      `/recordings/${encodeURIComponent(this.roomId)}/${encodeURIComponent(this.recordingId)}/abort`,
      { reason: String(reason || 'Browser recording aborted').slice(0, 500) },
      3
    );
  }

  async abort(reason = 'Browser recording aborted') {
    // A meeting exit/device failure is not permission to discard media that
    // the browser has already durably captured.
    if (this.directUpload) return this.preserveDirectCapture();
    if (this.abortPromise) return this.abortPromise;
    this.abortPromise = (async () => {
      try {
        if (this.recorder && this.recorder.state !== 'inactive') {
          await new Promise((resolve) => {
            const timer = window.setTimeout(resolve, 3000);
            this.recorder.addEventListener('stop', () => {
              window.clearTimeout(timer);
              resolve();
            }, { once: true });
            try {
              this.recorder.stop();
            } catch {
              window.clearTimeout(timer);
              resolve();
            }
          });
        }
        await this.uploadQueue.catch(() => undefined);
        return await this.abortServerCapture(reason);
      } finally {
        this.cleanup();
      }
    })();
    return this.abortPromise;
  }

  async stop() {
    if (this.startPromise) { await this.startPromise; return this.stop(); }
    if (this.preservePromise) return this.preservePromise;
    if (this.stopPromise) return this.stopPromise;
    if (!this.recorder || this.recorder.state === 'inactive') {
      throw new Error('No active recording');
    }
    this.stopPromise = (async () => {
      try {
        await new Promise((resolve, reject) => {
          this.recorder.addEventListener('stop', resolve, { once: true });
          this.recorder.addEventListener('error', (event) => reject(event.error), { once: true });
          this.recorder.requestData();
          this.recorder.stop();
        });
        await this.uploadQueue;
        if (this.uploadFailure) {
          const failure = this.uploadFailure;
          await this.abort(
            failure?.response?.data?.error ||
            failure?.message ||
            'Recording chunk upload failed'
          );
          throw failure;
        }
        if (this.directUpload) {
          const upload = this.directUpload;
          return await upload.finish();
        }
        const response = await this.postWithRetry(
          `/recordings/${encodeURIComponent(this.roomId)}/${encodeURIComponent(this.recordingId)}/stop`,
          undefined,
          3
        );
        return response.data;
      } catch (error) {
        if (this.directUpload) {
          // requestData(), stop(), and encoding can also fail before finish().
          // Keep their last asynchronous data event and local recovery session.
          if (this.recorder && this.recorder.state !== 'inactive') {
            try { this.recorder.stop(); } catch { /* Browser capture already ended. */ }
          }
          if (this.captureStoppedPromise) await this.captureStoppedPromise;
          await this.uploadQueue.catch(() => {});
          await this.directUpload.preserve(error).catch(() => {});
        }
        throw error;
      } finally {
        this.cleanup();
      }
    })();
    return this.stopPromise;
  }

  isActive() {
    return Boolean(this.recorder && this.recorder.state !== 'inactive');
  }

  getStatus() {
    return {
      roomId: this.roomId, recordingId: this.recordingId,
      state: this.isActive() ? 'recording' : this.startPromise ? 'starting' :
        (this.stopPromise || this.preservePromise || this.directUpload) ? 'saving' : 'idle',
    };
  }

  isSavingForRoom(roomId) {
    return this.roomId === String(roomId || '').trim() && Boolean(this.startPromise || this.stopPromise || this.preservePromise || this.directUpload);
  }

  isOwnerForRoom(roomId) {
    return this.isActive() && this.roomId === String(roomId || '').trim();
  }

  async stopIfActive(roomId = '') {
    if (roomId && this.roomId !== String(roomId).trim()) return null;
    // MediaRecorder becomes inactive before its last Blob and server commit.
    // Meeting end/navigation must wait through that finalization window.
    if (this.startPromise) { await this.startPromise; return this.stopIfActive(roomId); }
    if (this.stopPromise) return this.stopPromise;
    if (this.preservePromise) return this.preservePromise;
    if (!this.isActive()) return null;
    if (roomId && !this.isOwnerForRoom(roomId)) return null;
    return this.stop();
  }

  cleanup() {
    window.clearTimeout(this.animationFrame);
    window.clearInterval(this.audioRefreshTimer);
    window.clearInterval(this.blackboardRefreshTimer);
    this.outputStream?.getTracks().forEach((track) => track.stop());
    try { this.silentAudioSource?.stop(); this.silentAudioSource?.disconnect(); } catch {}
    this.silentAudioSource = null;
    this.audioContext?.close().catch(() => undefined);
    this.releasePrimedAudio();
    this.recorder = null;
    this.recordingId = '';
    this.outputStream = null;
    this.audioContext = null;
    this.audioRefreshTimer = 0;
    this.blackboardRefreshTimer = 0;
    this.blackboardImage = null;
    if (this.blackboardObjectUrl) URL.revokeObjectURL(this.blackboardObjectUrl);
    this.blackboardObjectUrl = '';
    for (const { source } of this.audioSources.values()) { try { source.disconnect(); } catch {} }
    this.audioSources.clear();
    this.connectedAudioTrackIds.clear();
    this.stopPromise = null;
    this.uploadQueue = Promise.resolve();
    this.uploadFailure = null;
    this.abortPromise = null;
    this.roomId = '';
    this.directUpload = null;
    this.captureStoppedPromise = null;
    this.preservePromise = null;
  }
}

export const meetingRecorder = new MeetingRecorder();
export default meetingRecorder;
